import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  Building2,
  CalendarRange,
  CheckCircle2,
  ClockArrowUp,
  Gift,
  Layers,
  Link2,
  Sparkles,
  TicketX,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, EmptyState, PageHeader, StatWell, Surface, Text } from '@/components/flup';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { getVendorProfile, vendorDashboardDetails } from '@/store/thunkMidddleware/usersMiddleware';
import routes from '@/routes/routes.json';

/**
 * The vendor's home, in the logip layout: a greeting, an inline row of stat
 * wells, and a right rail carrying identity and next steps.
 *
 * A deliberate omission — the reference has a performance line chart here, and
 * this page does not. `vendorDashboardDetails` returns lifetime totals with no
 * time dimension, so any trend line drawn here would be invented. The split bar
 * and the breakdown below show the same data honestly; when a time-series
 * endpoint exists, the chart belongs in the `Performance` slot marked below.
 */

interface DashboardData {
  revenue?: number;
  totalBookings?: number;
  completedBookings?: number;
  upComingBookings?: number;
  cancellOrFailedbooking?: number;
  activeVenues?: number;
  activeCourts?: number;
}

const inr = (n = 0) => `₹${Math.round(n).toLocaleString('en-IN')}`;

const DashboardHome: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { vendorProfile, vendorDashboardDetailsResponse } = useAppSelector((s) => s.profile);
  const [data, setData] = useState<DashboardData>({});

  useEffect(() => {
    dispatch(
      getVendorProfile({
        callback: () => {
          dispatch(
            vendorDashboardDetails({
              callback: (res: any) => res?.payload && setData(res.payload),
            }),
          );
        },
      }),
    );
  }, [dispatch]);

  const profile = vendorProfile?.payload;
  const profileComplete = Boolean(profile?.vendorProfileCompleted);
  const completion = profile?.profilePercentage ?? 0;
  const loading = vendorDashboardDetailsResponse.isLoading || vendorProfile.isLoading;

  const total = data.totalBookings || 0;
  const completed = data.completedBookings || 0;
  const upcoming = data.upComingBookings || 0;
  const lost = data.cancellOrFailedbooking || 0;

  /** The three outcomes as shares of one bar — the honest version of a chart. */
  const split = useMemo(() => {
    const sum = completed + upcoming + lost;
    if (!sum) return null;
    return {
      completed: (completed / sum) * 100,
      upcoming: (upcoming / sum) * 100,
      lost: (lost / sum) * 100,
      // How often a booking that was made actually got played.
      completionRate: Math.round((completed / sum) * 100),
    };
  }, [completed, upcoming, lost]);

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  const firstName = String(profile?.name || profile?.fullName || 'there').split(' ')[0];

  if (loading && !total) return <DashboardSkeleton />;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      {/* ------------------------------------------------------------- main */}
      <div className="space-y-4 min-w-0">
        <PageHeader
          title={`${greeting}, ${firstName}`}
          subtitle={
            profileComplete
              ? 'Here’s how your venues are doing.'
              : 'Finish setting up your profile to start taking bookings.'
          }
          actions={
            <Button
              icon={<CalendarRange className="w-4 h-4" />}
              onClick={() => navigate(routes.venueManagement)}
            >
              Manage slots
            </Button>
          }
        />

        {/* The logip stat row: circular wells on one line, divided. */}
        <Surface padding="p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:divide-x lg:divide-flup-hairline">
            <StatWell icon={<Layers />} label="Total bookings" value={total} />
            <StatWell
              icon={<CheckCircle2 />}
              label="Completed"
              value={completed}
              className="lg:pl-5"
            />
            <StatWell
              icon={<ClockArrowUp />}
              label="Upcoming"
              value={upcoming}
              className="lg:pl-5"
            />
            <StatWell
              icon={<Wallet />}
              label="Revenue"
              value={inr(data.revenue)}
              hint="completed only"
              className="lg:pl-5"
            />
          </div>
        </Surface>

        {/* Performance slot. A trend chart goes here the day the API can
            return bookings-over-time; until then this shows the same numbers
            without pretending to a history it doesn't have. */}
        <Surface padding="p-5">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <Text variant="h3" as="h2">
                Booking outcomes
              </Text>
              <Text variant="caption" tone="tertiary" as="p" className="mt-0.5">
                Every booking ever made across your venues
              </Text>
            </div>
            {split ? (
              <div className="text-right">
                <Text variant="statSm" as="p">
                  {split.completionRate}%
                </Text>
                <Text variant="micro" tone="tertiary" as="p">
                  played through
                </Text>
              </div>
            ) : null}
          </div>

          {split ? (
            <>
              <div className="flex h-2.5 rounded-pill overflow-hidden bg-flup-sunken">
                <span style={{ width: `${split.completed}%` }} className="bg-flup-success" />
                <span style={{ width: `${split.upcoming}%` }} className="bg-flup-info" />
                <span style={{ width: `${split.lost}%` }} className="bg-flup-danger" />
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                <Legend tint="bg-flup-success" label="Completed" value={completed} />
                <Legend tint="bg-flup-info" label="Upcoming" value={upcoming} />
                <Legend tint="bg-flup-danger" label="Cancelled or failed" value={lost} />
              </div>
            </>
          ) : (
            <EmptyState
              compact
              icon={<TrendingUp />}
              title="No bookings yet"
              message="Once customers start booking your slots, the breakdown shows up here."
            />
          )}
        </Surface>

        {/* Footprint + quick actions */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Surface padding="p-5">
            <Text variant="h3" as="h2" className="mb-4">
              Your footprint
            </Text>
            <div className="space-y-3">
              <FootprintRow
                icon={<Building2 className="w-4 h-4" />}
                label="Active venues"
                value={data.activeVenues ?? 0}
                onClick={() => navigate(routes.venueList)}
              />
              <FootprintRow
                icon={<CalendarRange className="w-4 h-4" />}
                label="Active courts"
                value={data.activeCourts ?? 0}
                onClick={() => navigate(routes.venueList)}
              />
              <FootprintRow
                icon={<TicketX className="w-4 h-4" />}
                label="Cancelled or failed"
                value={lost}
                onClick={() => navigate(routes.venueBookings)}
              />
            </div>
          </Surface>

          <Surface padding="p-5">
            <Text variant="h3" as="h2" className="mb-4">
              Quick actions
            </Text>
            <div className="space-y-2">
              <QuickAction
                icon={<CalendarRange className="w-4 h-4" />}
                title="Manage today's slots"
                description="Open, block or book any slot"
                onClick={() => navigate(routes.venueManagement)}
              />
              <QuickAction
                icon={<Building2 className="w-4 h-4" />}
                title="Add a venue"
                description="Onboard a new ground or arena"
                onClick={() => navigate(routes.venueOnboard)}
              />
              <QuickAction
                icon={<Gift className="w-4 h-4" />}
                title="Run an offer"
                description="Discount slots to fill quiet hours"
                onClick={() => navigate(routes.offerManagement)}
              />
              <QuickAction
                icon={<Link2 className="w-4 h-4" />}
                title="Share a booking link"
                description="Take bookings straight from your own channels"
                onClick={() => navigate(routes.DirectBookingLink)}
              />
            </div>
          </Surface>
        </div>
      </div>

      {/* ------------------------------------------------------------- rail */}
      <div className="space-y-4 min-w-0">
        <Surface padding="p-5">
          <div className="flex flex-col items-center text-center">
            {profile?.profileImage ? (
              <img
                src={profile.profileImage}
                alt=""
                className="w-16 h-16 rounded-pill object-cover bg-flup-sunken"
              />
            ) : (
              <span className="grid place-items-center w-16 h-16 rounded-pill bg-flup-green-soft">
                <Text variant="h2" as="span" className="text-flup-green-ink">
                  {firstName.charAt(0).toUpperCase()}
                </Text>
              </span>
            )}

            <Text variant="h3" as="p" className="mt-3">
              {profile?.name || profile?.fullName || 'Vendor'}
            </Text>
            {profile?.email ? (
              <Text variant="caption" tone="tertiary" as="p" lines={1} className="mt-0.5">
                {profile.email}
              </Text>
            ) : null}
          </div>

          <div className="mt-5 pt-4 border-t border-flup-hairline">
            <div className="flex items-center justify-between mb-2">
              <Text variant="caption" tone="secondary" as="span">
                Profile completion
              </Text>
              <Text variant="labelSemi" as="span" className="nums">
                {completion}%
              </Text>
            </div>
            <div className="h-1.5 rounded-pill bg-flup-sunken overflow-hidden">
              <span
                style={{ width: `${Math.min(100, completion)}%` }}
                className={cn(
                  'block h-full rounded-pill transition-[width] duration-500',
                  completion >= 100 ? 'bg-flup-success' : 'bg-flup-brand',
                )}
              />
            </div>

            {completion < 100 ? (
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                className="mt-3"
                icon={<AlertTriangle className="w-3.5 h-3.5" />}
                onClick={() => navigate(routes.vendorOnboard)}
              >
                Finish setup
              </Button>
            ) : null}
          </div>
        </Surface>

        {/* The assistant's home on this page. Deliberately present and clearly
            unfinished, rather than absent — the layout is budgeted for it now. */}
        <Surface padding="p-5" className="bg-flup-brand-soft border-flup-brand/20">
          <span className="grid place-items-center w-10 h-10 rounded-flup-sm bg-flup-surface/70 text-flup-brand-ink mb-3">
            <Sparkles className="w-[18px] h-[18px]" aria-hidden />
          </span>
          <Text variant="h3" as="p" className="text-flup-brand-ink">
            Ask about your venues
          </Text>
          <Text variant="caption" as="p" className="mt-1 text-flup-brand-ink/70">
            Soon you'll be able to ask things like “which court earns least on
            weekday mornings?” and act on the answer without leaving this page.
          </Text>
          <span className="inline-flex items-center gap-1 mt-3 px-2 h-6 rounded-flup-xs bg-flup-surface/70">
            <Text variant="micro" as="span" className="text-flup-brand-ink">
              Coming soon
            </Text>
          </span>
        </Surface>
      </div>
    </div>
  );
};

const Legend: React.FC<{ tint: string; label: string; value: number }> = ({
  tint,
  label,
  value,
}) => (
  <span className="inline-flex items-center gap-2">
    <span className={cn('w-2 h-2 rounded-pill shrink-0', tint)} aria-hidden />
    <Text variant="caption" tone="secondary" as="span">
      {label}
    </Text>
    <Text variant="labelSemi" as="span" className="nums">
      {value}
    </Text>
  </span>
);

const FootprintRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  onClick: () => void;
}> = ({ icon, label, value, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-3 w-full p-2.5 -mx-2.5 rounded-flup-sm hover:bg-flup-surface-alt transition-colors text-left"
  >
    <span className="grid place-items-center w-8 h-8 rounded-flup-xs bg-flup-surface-alt text-flup-ink-2 shrink-0">
      {icon}
    </span>
    <Text variant="bodyMedium" as="span" className="flex-1 min-w-0">
      {label}
    </Text>
    <Text variant="statSm" as="span">
      {value}
    </Text>
  </button>
);

const QuickAction: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex items-center gap-3 w-full p-3 rounded-flup-md border border-flup-hairline hover:border-flup-green hover:bg-flup-green-soft transition-colors text-left"
  >
    <span className="grid place-items-center w-9 h-9 rounded-flup-sm bg-flup-surface-alt text-flup-ink-2 shrink-0 group-hover:bg-flup-surface group-hover:text-flup-green-ink transition-colors">
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <Text variant="labelSemi" as="span" className="block">
        {title}
      </Text>
      <Text variant="micro" tone="tertiary" as="span" className="block mt-0.5">
        {description}
      </Text>
    </span>
    <ArrowUpRight
      className="w-4 h-4 text-flup-ink-3 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      aria-hidden
    />
  </button>
);

const DashboardSkeleton = () => (
  <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
    <div className="space-y-4">
      <div className="h-10 w-64 rounded-flup-sm bg-flup-surface animate-pulse" />
      <div className="h-[104px] rounded-flup-lg bg-flup-surface border border-flup-hairline animate-pulse" />
      <div className="h-[180px] rounded-flup-lg bg-flup-surface border border-flup-hairline animate-pulse" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="h-[220px] rounded-flup-lg bg-flup-surface border border-flup-hairline animate-pulse" />
        <div className="h-[220px] rounded-flup-lg bg-flup-surface border border-flup-hairline animate-pulse" />
      </div>
    </div>
    <div className="space-y-4">
      <div className="h-[260px] rounded-flup-lg bg-flup-surface border border-flup-hairline animate-pulse" />
      <div className="h-[180px] rounded-flup-lg bg-flup-surface border border-flup-hairline animate-pulse" />
    </div>
  </div>
);

export default DashboardHome;
